// PURE model of the DOM FX layer (#fx, z5; IMPLEMENTATION §2.6 layer model, UX §5 "ItemSold", "Purchased",
// "GalleryFired", "Discovered", caps table): coin flights, "+$X" floaters and NEW! ribbons / stat labels as
// plain records with launch times, positions evaluated in closed form at any time (fx/DomFx.ts only copies
// them onto pooled nodes). Caps (juice.json caps / aggregation):
//   coins     ≤ caps.coinFlights (20) in flight, staggered ones included; a burst that does not fit becomes
//             ONE "coin bag" flight, at most one bag per aggregation.coinBagMs (250 ms) — beyond that the
//             value only ticks the HUD (M7);
//   floaters  ≤ caps.floaters (16) alive; a value from a source whose floater started < floaterMs (250 ms)
//             ago is added to it; at the cap a new source recycles the oldest floater; a Gallery's shots are
//             summed into ONE floater per galleryFloaterMs (1 s; reduce motion: 2 s) per Gallery;
//   ribbons   ≤ caps.ribbons (3) NEW! ribbons / stat labels; the oldest goes, near ones stack a row up.
// Randomness (the coins' arc spread) comes from the injected `rand`, so tests are deterministic.

export interface Pt {
  x: number;
  y: number;
}

export interface DomFxCfg {
  coinCap: number;
  floaterCap: number;
  floaterAggMs: number;
  coinBagMs: number;
  /** juice.json caps.ribbons. */
  ribbonCap: number;
  /** juice.json events.discover: a ribbon row = its font size × ribbonRowEm; "near" = within ribbonNearRows rows. */
  ribbonRowEm: number;
  ribbonNearRows: number;
  /** juice.json events.sell: sizes, counts, flight. */
  sell: {
    floaterRisePx: number;
    floaterMs: number;
    floaterMinPx: number;
    floaterMaxPx: number;
    floaterFullAt: number;
    coinsSmall: number;
    coinsMedium: number;
    coinsLarge: number;
    coinsMediumAt: number;
    coinsLargeAt: number;
    coinStaggerMs: number;
    coinFlightMs: number;
    coinPx: number;
    coinArcPx: number;
    /** The arc height is coinArcPx × (coinArcMin + (1 − coinArcMin) × random). */
    coinArcMin: number;
    /** A coin starts this share of its sideways spread off the source. */
    coinStartSpread: number;
    coinSpreadPx: number;
    bagPx: number;
  };
}

/** One coin (or coin bag) flight on a quadratic Bézier arc p0 → c → p1. */
export interface Coin {
  key: number;
  x0: number;
  y0: number;
  cx: number;
  cy: number;
  x1: number;
  y1: number;
  start: number;
  ms: number;
  px: number;
  bag: boolean;
  /** An arrival at the HUD pops it (the `gm:fx-arrive` DOM event); purchases fly the other way. */
  toHud: boolean;
}

export interface Floater {
  key: number;
  src: string;
  value: number;
  x: number;
  y: number;
  start: number;
  ms: number;
  px: number;
  rise: number;
}

/** A NEW! ribbon (name of a new tier) or a stat label ("Lead Vein L2"). */
export interface Ribbon {
  key: number;
  kind: 'new' | 'label';
  text: string;
  x: number;
  y: number;
  start: number;
  ms: number;
  px: number;
  rise: number;
}

export interface DomFxStats {
  coins: number;
  coinsPeak: number;
  coinsLaunched: number;
  bags: number;
  coinsDropped: number;
  floaters: number;
  floatersPeak: number;
  floatersShown: number;
  floatersMerged: number;
  ribbons: number;
}

interface GalleryWindow {
  value: number;
  start: number;
  at: Pt;
  px: number;
  ms: number;
}

/** Coins for a sale of `value` against 60 s of income (UX §5: 3 / 6 / 12). */
export function coinCount(value: number, income60: number, s: DomFxCfg['sell']): number {
  const r = value / Math.max(1, income60);
  return r >= s.coinsLargeAt ? s.coinsLarge : r >= s.coinsMediumAt ? s.coinsMedium : s.coinsSmall;
}

/**
 * Floater font size: log-scaled by value vs 60 s of income, `floaterMinPx` … `floaterMaxPx` (full size at
 * `floaterFullAt` × the minute's income).
 */
export function floaterPx(value: number, income60: number, s: DomFxCfg['sell']): number {
  const r = value / Math.max(1, income60) / Math.max(1e-9, s.floaterFullAt);
  const k = Math.max(0, Math.min(1, Math.log10(1 + 9 * r)));
  return Math.round(s.floaterMinPx + (s.floaterMaxPx - s.floaterMinPx) * k);
}

/** Smoothstep: coins ease out of the burst and into the HUD. */
function ease(t: number): number {
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return c * c * (3 - 2 * c);
}

/** A coin's position at `now` (null before its launch or after it landed). */
export function coinAt(c: Coin, now: number): Pt | null {
  const t = (now - c.start) / c.ms;
  if (t < 0 || t > 1) return null;
  const u = ease(t);
  const a = (1 - u) * (1 - u);
  const b = 2 * (1 - u) * u;
  const d = u * u;
  return { x: a * c.x0 + b * c.cx + d * c.x1, y: a * c.y0 + b * c.cy + d * c.y1 };
}

export class DomFxModel {
  coins: Coin[] = [];
  floaters: Floater[] = [];
  ribbons: Ribbon[] = [];
  private readonly c: DomFxCfg;
  private readonly rand: () => number;
  private nextKey = 1;
  private lastBag = Number.NEGATIVE_INFINITY;
  private readonly windows = new Map<string, GalleryWindow>();
  private coinsPeakN = 0;
  private floatersPeakN = 0;
  private launched = 0;
  private bagsN = 0;
  private droppedN = 0;
  private shownN = 0;
  private mergedN = 0;

  constructor(cfg: DomFxCfg, rand: () => number) {
    this.c = cfg;
    this.rand = rand;
  }

  private coin(
    from: Pt,
    to: Pt,
    start: number,
    ms: number,
    px: number,
    bag: boolean,
    toHud: boolean,
  ): void {
    const s = this.c.sell;
    const spread = (this.rand() - 0.5) * 2 * s.coinSpreadPx;
    // control point: above the midpoint (the arc bows up toward the HUD), pushed sideways by the spread
    const cx = (from.x + to.x) / 2 + spread;
    const cy =
      Math.min(from.y, to.y) - s.coinArcPx * (s.coinArcMin + (1 - s.coinArcMin) * this.rand());
    this.coins.push({
      key: this.nextKey++,
      x0: from.x + spread * s.coinStartSpread,
      y0: from.y,
      cx,
      cy,
      x1: to.x,
      y1: to.y,
      start,
      ms,
      px,
      bag,
      toHud,
    });
    this.launched += 1;
    this.coinsPeakN = Math.max(this.coinsPeakN, this.coins.length);
  }

  /**
   * A burst of `n` coins from `from` to `to` (staggered). If they do not fit under the cap, ONE coin bag
   * flies instead (at most one per coinBagMs); otherwise nothing flies. Returns what was launched.
   */
  coinBurst(
    n: number,
    from: Pt,
    to: Pt,
    now: number,
    o: { ms: number; toHud: boolean },
  ): 'coins' | 'bag' | 'none' {
    if (n <= 0) return 'none';
    const s = this.c.sell;
    const room = this.c.coinCap - this.coins.length;
    if (n <= room) {
      for (let i = 0; i < n; i++)
        this.coin(from, to, now + i * s.coinStaggerMs, o.ms, s.coinPx, false, o.toHud);
      return 'coins';
    }
    if (room >= 1 && now - this.lastBag >= this.c.coinBagMs) {
      this.lastBag = now;
      this.bagsN += 1;
      this.coin(from, to, now, o.ms, s.bagPx, true, o.toHud);
      return 'bag';
    }
    this.droppedN += n;
    return 'none';
  }

  /** "+$X" from `src` at `at`: merged into that source's floater younger than floaterAggMs, else a new one. */
  floater(src: string, value: number, at: Pt, now: number, px: number): void {
    const s = this.c.sell;
    let same: Floater | null = null;
    for (const f of this.floaters)
      if (f.src === src && (same === null || f.start > same.start)) same = f;
    if (same !== null && now - same.start < this.c.floaterAggMs) {
      same.value += value;
      same.px = Math.max(same.px, px);
      this.mergedN += 1;
      return;
    }
    if (this.floaters.length >= this.c.floaterCap) {
      if (same !== null) {
        // at the cap: this source's floater takes the value (it stays up a little longer)
        same.value += value;
        same.start = now;
        same.px = Math.max(same.px, px);
        this.mergedN += 1;
        return;
      }
      // a new source recycles the oldest floater
      let oldest = 0;
      for (let i = 1; i < this.floaters.length; i++)
        if ((this.floaters[i] as Floater).start < (this.floaters[oldest] as Floater).start)
          oldest = i;
      this.floaters.splice(oldest, 1);
    }
    this.floaters.push({
      key: this.nextKey++,
      src,
      value,
      x: at.x,
      y: at.y,
      start: now,
      ms: s.floaterMs,
      px,
      rise: s.floaterRisePx,
    });
    this.shownN += 1;
    this.floatersPeakN = Math.max(this.floatersPeakN, this.floaters.length);
  }

  /** A Gallery shot's payout: summed per Gallery; one floater when its window (`windowMs`) closes. */
  galleryShot(src: string, value: number, at: Pt, now: number, px: number, windowMs: number): void {
    const w = this.windows.get(src);
    if (w === undefined) this.windows.set(src, { value, start: now, at, px, ms: windowMs });
    else {
      w.value += value;
      w.at = at;
      w.px = Math.max(w.px, px);
    }
  }

  /**
   * A NEW! ribbon or a stat label; beyond the ribbon cap the oldest goes. Ribbons still up near the new one
   * move up a row (they stack instead of covering each other).
   */
  ribbon(
    kind: Ribbon['kind'],
    text: string,
    at: Pt,
    now: number,
    ms: number,
    px: number,
    rise: number,
  ): void {
    if (this.ribbons.length >= this.c.ribbonCap) this.ribbons.shift();
    const row = px * this.c.ribbonRowEm;
    for (const r of this.ribbons)
      if (Math.abs(r.x - at.x) < row * this.c.ribbonNearRows && Math.abs(r.y - at.y) < row)
        r.y -= row;
    this.ribbons.push({
      key: this.nextKey++,
      kind,
      text,
      x: at.x,
      y: at.y,
      start: now,
      ms,
      px,
      rise,
    });
  }

  /**
   * Advances to `now`: closes due Gallery windows (their floaters appear), drops finished coins / floaters /
   * ribbons. Returns how many coins landed on the HUD this step.
   */
  step(now: number): number {
    for (const [src, w] of this.windows) {
      if (now - w.start < w.ms) continue;
      this.windows.delete(src);
      this.floater(src, w.value, w.at, now, w.px);
    }
    let landed = 0;
    if (this.coins.length > 0) {
      const keep: Coin[] = [];
      for (const c of this.coins) {
        if (now - c.start >= c.ms) {
          if (c.toHud) landed += 1;
        } else keep.push(c);
      }
      this.coins = keep;
    }
    if (this.floaters.some((f) => now - f.start >= f.ms))
      this.floaters = this.floaters.filter((f) => now - f.start < f.ms);
    if (this.ribbons.some((r) => now - r.start >= r.ms))
      this.ribbons = this.ribbons.filter((r) => now - r.start < r.ms);
    return landed;
  }

  /** Something is still flying / floating / waiting for its Gallery window. */
  busy(): boolean {
    return (
      this.coins.length > 0 ||
      this.floaters.length > 0 ||
      this.ribbons.length > 0 ||
      this.windows.size > 0
    );
  }

  clear(): void {
    this.coins = [];
    this.floaters = [];
    this.ribbons = [];
    this.windows.clear();
  }

  resetPeaks(): void {
    this.coinsPeakN = this.coins.length;
    this.floatersPeakN = this.floaters.length;
  }

  stats(): DomFxStats {
    return {
      coins: this.coins.length,
      coinsPeak: this.coinsPeakN,
      coinsLaunched: this.launched,
      bags: this.bagsN,
      coinsDropped: this.droppedN,
      floaters: this.floaters.length,
      floatersPeak: this.floatersPeakN,
      floatersShown: this.shownN,
      floatersMerged: this.mergedN,
      ribbons: this.ribbons.length,
    };
  }
}
